import type { Key, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import RCTree from 'rc-tree/es';
import { conductExpandParent } from 'rc-tree/es/util';
import { convertDataToEntities } from 'rc-tree/es/utils/treeUtil';
import type { EventDataNode, TreeNodeProps } from 'rc-tree/es/interface';
import { TreeContextProvider } from '../../../contexts/TreeContext';
import chevronRight from '@ingka/ssr-icon/paths/chevron-right-small';
import NodeIcon from '../../Icons/NodeIcon/NodeIcon';
import { getUnfoldedNodeIds } from '../helpers/folding';
import NodeContent from '../NodeContent';
import styles from './CommonTree.module.scss';
import type { NodeBase } from '../../../entities/Node';
import type { OnSelectEvent, OnExpandType } from '../treeTypes';
import SSRIcon from '@ingka/ssr-icon';
import { closeModal, openModal } from '../../../store/actions/modal/modal';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetTreeSorting,
  selectSortedCatalogs,
} from '../../../store/slices/treeSort';

interface DropInfo {
  node: NodeBase;
  dragNode: NodeBase;
  dropPosition: number;
  dropToGap: boolean;
}

interface DragState {
  isDragging: boolean;
  dragNodeKey: Key | null;
}

interface CommonTreeProps {
  className?: string;
  indentTree: boolean;
  autoExpandParent: boolean;
  onActiveChange?: (key: Key) => void;
  onDoubleClick?: (e: unknown, node: { type: string }) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onSelect?: (selectedKeys: React.Key[], info: OnSelectEvent) => void;
  onExpand: OnExpandType;
  onDrop?: (info: DropInfo) => void;
  scrollToKey: string | number | null;
  selectedKeys: string[];
  treeNodes: NodeBase[];
  pane?: string;
  draggable?: boolean;
  allowRanking?: boolean;
  allowRankingLevels?: number[];
  catalogId?: string;
}

const getParentKey = (key: Key): string => {
  return key.toString().split(':').slice(0, -1).join(':');
};

const CommonTree = ({
  className,
  catalogId,
  indentTree,
  onSelect,
  onExpand,
  onDrop,
  scrollToKey,
  selectedKeys = [],
  treeNodes,
  pane,
  draggable = false,
  allowRanking = false,
  allowRankingLevels = [],
  ...props
}: CommonTreeProps) => {
  const ref = useRef<RCTree<NodeBase> | null>(null);
  const sortedCatalogs = useSelector(selectSortedCatalogs);
  const dispatch = useDispatch();
  const hasUnsavedSorting = catalogId && sortedCatalogs[catalogId];
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragNodeKey: null,
  });
  const onDragStart = useCallback(
    ({ node }: { event: React.DragEvent; node: EventDataNode<NodeBase> }) => {
      if (node.type === 'category') {
        setDragState({ isDragging: true, dragNodeKey: node.key });
      }
    },
    [],
  );

  const onDragEnd = useCallback(() => {
    setDragState({ isDragging: false, dragNodeKey: null });
  }, []);
  const iconRenderer = useCallback((nodeProps: TreeNodeProps) => {
    return <NodeIcon {...nodeProps} key={nodeProps?.data?.key} />;
  }, []);

  const titleRenderer = useCallback((nodeData: NodeBase) => {
    return <NodeContent node={nodeData} key={nodeData.key} />;
  }, []);

  const onExpandNode = useCallback(
    (
      _: unknown,
      treeNode: {
        node: EventDataNode<NodeBase>;
        expanded: boolean;
        nativeEvent: MouseEvent;
      },
    ) => {
      // Block expansion while dragging to prevent accidental parent changes
      if (dragState.isDragging) {
        return;
      }
      onExpand(treeNode);
    },
    [onExpand, dragState.isDragging],
  );

  const allowDrop = useCallback(
    ({
      dragNode,
      dropNode,
      dropPosition,
    }: {
      dragNode: NodeBase;
      dropNode: NodeBase;
      dropPosition: number;
    }) => {
      const isRankingAllowed =
        allowRankingLevels.length > 0
          ? allowRankingLevels.includes(dragNode.level)
          : allowRanking;

      if (!isRankingAllowed) {
        return false;
      }

      const dragParentKey = getParentKey(dragNode.key);
      const dropParentKey = getParentKey(dropNode.key);

      // drop to pos 0
      if (dropPosition === 0 && dropNode.key === dragParentKey) {
        return true;
      }

      // Only allow drop if both nodes share the same parent
      const isSameParent = dragParentKey === dropParentKey;

      // dropPosition: -1 = above (gap top), 0 = onto (as child), 1 = below (gap bottom)
      const isValidPosition = dropPosition !== 0;

      return isSameParent && isValidPosition;
    },
    [allowRanking, allowRankingLevels],
  );

  const handleDrop = useCallback(
    (info: DropInfo) => {
      if (onDrop) {
        onDrop(info);
      }
    },
    [onDrop],
  );

  const expandedNodes = useMemo(() => {
    const expanded = getUnfoldedNodeIds(treeNodes);
    if (!scrollToKey) {
      return expanded;
    }

    const { keyEntities } = convertDataToEntities(treeNodes);
    const expandKeys = [
      ...new Set([
        ...expanded,
        ...conductExpandParent([scrollToKey], keyEntities).filter(
          (key) => key !== scrollToKey,
        ),
      ]),
    ];
    return expandKeys;
  }, [scrollToKey, treeNodes]);

  useEffect(() => {
    if (scrollToKey && ref.current) {
      ref.current.scrollTo({
        align: 'top',
        key: scrollToKey,
      });
    }
  }, [ref, scrollToKey]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Find the element behind the overlay so we can retrigger the click on cancel
      const overlay = e.currentTarget;
      overlay.style.pointerEvents = 'none';
      const targetBehind = document.elementFromPoint(
        e.clientX,
        e.clientY,
      ) as HTMLElement | null;
      overlay.style.pointerEvents = '';

      dispatch(
        openModal('ConfirmDialog', {
          asPopup: true,
          labelCancel: 'Leave without saving',
          labelConfirm: 'Go back and continue editing',
          message: 'You will lose your unsaved changes if you leave without saving.',
          onCancel: () => {
            if (catalogId) {
              dispatch(resetTreeSorting({ catalogId }));
            }
            dispatch(closeModal());
            targetBehind?.click();
          },
          onClose: () => {
            dispatch(closeModal());
          },
          onConfirm: () => {
            dispatch(closeModal());
          },
          title: 'You have unsaved sorting changes!',
        }),
      );
    },
    [catalogId, dispatch],
  );

  // Fix WCAG scrollable-region-focusable: add tabIndex to the inner rc-tree-list
  useEffect(() => {
    if (pane) {
      const treeList = document.querySelector(
        `.rc-tree-list[data-testid="${pane}"]`,
      );
      if (treeList && !treeList.hasAttribute('tabindex')) {
        treeList.setAttribute('tabindex', '0');
      }
    }
  }, [pane, treeNodes]);

  const height = window.innerHeight - 152; // header + catalog selector

  const dropIndicatorRender = useCallback(() => {
    return <div className={styles.dropIndicator} />;
  }, []);

  return (
    <TreeContextProvider>
      {hasUnsavedSorting && (
        <div
          className={styles.unsavedOverlay}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}
      <div className={styles.treeWrapper}>
        <RCTree
          data-testid={pane}
          ref={ref}
          focusable
          height={height}
          tabIndex={0}
          className={cx(
            { [styles.indent]: indentTree },
            { [styles.isDragging]: dragState.isDragging },
            className,
          )}
          itemHeight={32}
          multiple={!!onSelect}
          onSelect={onSelect}
          selectable={!!onSelect}
          selectedKeys={selectedKeys}
          treeData={treeNodes}
          {...props}
          virtual={false}
          draggable={
            draggable
              ? { nodeDraggable: (node) => (node as NodeBase).type === 'category' }
              : false
          }
          allowDrop={allowDrop}
          onDrop={handleDrop}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          dropIndicatorRender={dropIndicatorRender}
          expandedKeys={expandedNodes}
          icon={iconRenderer}
          onExpand={onExpandNode}
          showIcon
          switcherIcon={<SSRIcon paths={chevronRight} />}
          titleRender={titleRenderer}
        />
      </div>
    </TreeContextProvider>
  );
};

export default CommonTree;
